import { Button, DatePicker, Descriptions, Form, Grid, Input, Radio, Select, Skeleton, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useReducer, useState } from 'react';
import { APOLLO_DATE_FORMAT, DATE_FORMAT } from '../../../../../../../../constants';
import {
  CredentialVerificationCaseV1,
  CredentialVerificationCaseV1Application,
  CredentialVerificationCaseV1RequestedChanges,
  CredentialVerificationCaseV1RevisionRequest,
  Entity
} from '../../../../../../../../generated';
import { Country } from '../../../../../../../../types/country';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { StateSelect } from '../../../../../shared/components/state-select';
import { CredCaseFileUploadButtonWithPreviewContainer } from '../../../../../../../shared/file-upload-button-with-preview/cred-case-file-upload-button-with-preview.container';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface CaseDetailsApplicationProps {
  data: CredentialVerificationCaseV1;
  application: CredentialVerificationCaseV1Application;
  applicantId: string;
  revisionRequested: CredentialVerificationCaseV1RevisionRequest;
  countryData: Country[];
  entities: Entity[];
  onSave: (input: CredentialVerificationCaseV1Application) => Promise<void>;
  onSaveAndComplete: (input: CredentialVerificationCaseV1Application, revisionRequest: CredentialVerificationCaseV1RevisionRequest) => Promise<void>;
  handleOnUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
}

interface ApplicationFormState {
  isApplicationEditable: boolean;
  isCredentialDetailsEditable: boolean;
  isIssuingInstitutionEditable: boolean;
  isUpload: boolean;
  requestedChanges: CredentialVerificationCaseV1RequestedChanges;
}
const formatDate = (date: string) => {
  return dayjs(date).utc().format(DATE_FORMAT).toUpperCase();
};
export const CaseDetailsApplication: React.FC<CaseDetailsApplicationProps> = (props) => {
  const labelStyle = {
    fontSize: '12px'
  };

  const descriptionStyle = {
    paddingLeft: '8px'
  };
  const { application, revisionRequested } = props;
  const [form] = Form.useForm();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(application?.issuingInstitution?.address?.country ?? null);
  const initialStage: ApplicationFormState = {
    isApplicationEditable: false,
    isCredentialDetailsEditable: false,
    isIssuingInstitutionEditable: false,
    isUpload: false,
    requestedChanges: {
      requestUpdatedCredentialDetails: revisionRequested?.requestedChanges?.requestUpdatedCredentialDetails,
      requestUpdatedIssuingInstitution: revisionRequested?.requestedChanges?.requestUpdatedIssuingInstitution,
      requestUploadCredential: revisionRequested?.requestedChanges?.requestUploadCredential,
      requestUploadTranslation: revisionRequested?.requestedChanges?.requestUploadTranslation
    }
  };

  const reducer = (state: ApplicationFormState, action: { type: string }) => {
    switch (action.type) {
      case 'edit-credential-details':
        return {
          ...state,
          isApplicationEditable: true,
          isCredentialDetailsEditable: true,
          requestedChanges: {
            ...state.requestedChanges,
            requestUpdatedCredentialDetails: false
          }
        };
      case 'edit-issuing-institution':
        return {
          ...state,
          isApplicationEditable: true,
          isIssuingInstitutionEditable: true,
          requestedChanges: {
            ...state.requestedChanges,
            requestUpdatedIssuingInstitution: false
          }
        };
      case 'upload':
        return {
          ...state,
          isApplicationEditable: true,
          isUpload: true,
          requestedChanges: {
            ...state.requestedChanges,
            requestUploadCredential: false,
            requestUploadTranslation: false
          }
        };
      case 'cancel':
        return {
          isCredentialDetailsEditable: false,
          isIssuingInstitutionEditable: false,
          isUpload: false,
          isApplicationEditable: false,
          requestedChanges: { ...revisionRequested.requestedChanges }
        };
      case 'save':
        return { ...initialStage };
      case 'save-complete':
        return { ...initialStage };
      default:
        return state;
    }
  };

  const [applicationFormState, dispatch] = useReducer(reducer, initialStage);
  const [isOtherIssuingInstitution, setIsOtherIssuingInstitution] = useState<boolean>(!application?.issuingInstitution?.issuingInstitution);

  const [formLoading, setFormLoading] = useState<boolean>(false);

  const handleSave = () => {
    setFormLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        form.submit();

        if (values.dateCredentialIssuedAt) {
          values.dateCredentialIssuedAt = dayjs(values.dateCredentialIssuedAt).utc().format(APOLLO_DATE_FORMAT);
        }

        if (values.issuingInstitution === 'other') {
          values.issuingInstitution = null;
        }

        await props.onSave(values as CredentialVerificationCaseV1Application);
        if (values.issuingInstitution !== null) {
          form.setFieldsValue({
            otherIssuingInstitution: {
              institutionName: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              zipOrPostalCode: '',
              stateOrProvince: '',
              country: ''
            }
          });
        }
        dispatch({ type: 'save' });
        setFormLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setFormLoading(false);
      });
  };

  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  const handleSaveAndComplete = () => {
    setFormLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        form.submit();

        if (values.dateCredentialIssuedAt) {
          values.dateCredentialIssuedAt = dayjs(values.dateCredentialIssuedAt).utc().format(APOLLO_DATE_FORMAT);
        }

        if (values.issuingInstitution === 'other') {
          values.issuingInstitution = null;
        }

        await props.onSaveAndComplete(values, {
          requestedChanges: applicationFormState.requestedChanges,
          revisionSubmittedAt: new Date().toISOString()
        });
        if (values.issuingInstitution !== null) {
          form.setFieldsValue({
            otherIssuingInstitution: {
              institutionName: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              zipOrPostalCode: '',
              stateOrProvince: '',
              country: ''
            }
          });
        }
        dispatch({ type: 'save-complete' });
        setFormLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setFormLoading(false);
      });
  };

  const isCredentialInEnglish = Form.useWatch('isCredentialInEnglish', form);

  if (formLoading) {
    return <Skeleton active />;
  }

  return (
    <div style={{ paddingBottom: '100px' }}>
      <Descriptions labelStyle={labelStyle} layout="vertical" column={isMobile ? 1 : 3}>
        <Descriptions.Item label="Submission Date">
          {props.data.submittedAt
            ? dayjs(props.data.submittedAt ?? '')
                .utc()
                .format(DATE_FORMAT)
                .toUpperCase()
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Decision Date">
          {props.data.caseDetails?.applicationReview?.decision?.completedAt ? formatDate(props.data.caseDetails?.applicationReview?.decision?.completedAt) : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Case Id">{props.data.id}</Descriptions.Item>
        <Descriptions.Item label="Decision">{props.data.caseDetails?.applicationReview?.decision?.result ?? 'Pending'}</Descriptions.Item>
      </Descriptions>
      {!applicationFormState.isUpload && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography.Text style={{ fontWeight: '400', fontSize: '16px' }}>What type of credential are you requesting to be verified?</Typography.Text>
            <Button
              type="primary"
              onClick={() => {
                dispatch({ type: 'upload' });
              }}
              disabled={!applicationFormState.requestedChanges.requestUploadCredential && !applicationFormState.requestedChanges.requestUploadTranslation}
            >
              Edit
            </Button>
          </div>
          <Typography.Text
            style={{
              ...descriptionStyle,
              margin: '12px 0px',
              fontWeight: '600',
              fontSize: '17px'
            }}
          >
            {application?.credentialType === 'primary' ? 'Primary Professional Credential' : 'Other Credential'}
          </Typography.Text>
          <Typography.Paragraph style={{ ...descriptionStyle }}>
            {application?.credentialType === 'primary'
              ? 'This is the credential issued by your educational institution after you have completed all requirements for and been awarded your degree qualifying you to practice in your profession. If your credential is not uploaded in the appropriate document category, ECFMG may correct the document category.'
              : 'This is any other credential type related to your professional practice that you may need to have verified. Examples include transcripts, specialty qualifications, licenses, or other credentials.'}
          </Typography.Paragraph>
          <Typography.Text
            style={{
              fontWeight: '400',
              fontSize: '16px',
              paddingBottom: '4px'
            }}
          >
            Uploaded Credential
          </Typography.Text>
          <div style={{ ...descriptionStyle, marginBottom: '20px' }}>
            <CredCaseFileUploadButtonWithPreviewContainer
              caseId={props.data.id}
              title={''}
              buttonText={'Upload Credential'}
              permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
              permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
              isPreviewOnly={true}
              isShowFileName={false}
              fieldName={'credential'}
              fileName={'credential'}
              onUploadSuccess={(response) => {
                props.handleOnUploadSuccess(response, 'credential');
              }}
            />
          </div>
          <Typography.Text style={{ fontWeight: '400', fontSize: '16px' }}>Is the credential you uploaded in English?</Typography.Text>
          <Typography.Text
            style={{
              ...descriptionStyle,
              fontWeight: '400',
              fontSize: '16px',
              paddingBottom: '4px'
            }}
          >
            {application.isCredentialInEnglish ? (
              <Typography.Paragraph style={descriptionStyle}>
                <Typography.Text strong>Yes.</Typography.Text>
              </Typography.Paragraph>
            ) : (
              <Typography.Paragraph style={descriptionStyle}>
                <Typography.Text strong>No,</Typography.Text> I will provide an English translation that meets ECFMG's translation requirements.
              </Typography.Paragraph>
            )}
          </Typography.Text>
          {!application.isCredentialInEnglish && (
            <div style={{ ...descriptionStyle, marginBottom: '20px' }}>
              <CredCaseFileUploadButtonWithPreviewContainer
                caseId={props.data.id}
                title={''}
                buttonText={'Upload English Translation'}
                permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                isPreviewOnly={true}
                isShowFileName={false}
                permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
                fieldName={'translation'}
                fileName={'translation'}
                onUploadSuccess={(response) => {
                  props.handleOnUploadSuccess(response, 'translation');
                }}
              />
            </div>
          )}
        </>
      )}
      {applicationFormState.isUpload && (
        <Form
          form={form}
          style={{ width: '100%' }}
          layout="vertical"
          initialValues={{
            credentialType: application?.credentialType ?? 'primary',
            isCredentialInEnglish: application?.isCredentialInEnglish ?? true
          }}
        >
          <Form.Item
            name={['credentialType']}
            valuePropName="value"
            getValueProps={(value) => ({
              value: value
            })}
            rules={[
              {
                required: true
              }
            ]}
            label={
              <Typography.Paragraph style={isMobile ? { fontSize: '16px', fontWeight: 'bold' } : { fontSize: '18px', fontWeight: '500' }}>
                What type of credential is it?
              </Typography.Paragraph>
            }
          >
            <Radio.Group defaultValue={'primary'} size="large" style={{ marginLeft: '10px' }}>
              <Space direction="vertical" style={{ rowGap: '0px' }}>
                <Radio style={isMobile ? { fontSize: '14px', fontWeight: 'bold' } : { fontSize: '20px', fontWeight: 'bold' }} value="primary">
                  Primary Professional Credential{' '}
                </Radio>
                <Typography.Paragraph style={{ marginLeft: '24px' }}>
                  This is the credential issued by your educational institution after you have completed all requirements for and been awarded your degree qualifying you to
                  practice in your profession. If your credential is not uploaded in the appropriate document category, ECFMG may correct the document category.{' '}
                </Typography.Paragraph>
                <Radio style={isMobile ? { fontSize: '14px', fontWeight: 'bold' } : { fontSize: '20px', fontWeight: 'bold' }} value="other">
                  Other Credential
                </Radio>
                <Typography.Paragraph style={{ marginLeft: '24px' }}>
                  This is any other credential type related to your professional practice that you may need to have verified. Examples include transcripts, specialty
                  qualifications, licenses, or other credentials.{' '}
                </Typography.Paragraph>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item name={['uploadCredential']} label={<Typography.Paragraph style={{ fontSize: '18px', fontWeight: '500' }}>Upload Credential</Typography.Paragraph>}>
            <CredCaseFileUploadButtonWithPreviewContainer
              caseId={props.data.id}
              title={''}
              buttonText={'Upload Revised Document'}
              permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
              permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
              isPreviewOnly={!applicationFormState.isUpload || !props.revisionRequested.requestedChanges?.requestUploadCredential}
              isShowFileName={false}
              fieldName={'credential'}
              fileName={'credential'}
              onUploadSuccess={(response) => {
                props.handleOnUploadSuccess(response, 'credential');
              }}
              cropAllowed={false}
            />
          </Form.Item>
          <Form.Item
            name={['isCredentialInEnglish']}
            getValueProps={(value) => {
              return {
                value: value
              };
            }}
            valuePropName="value"
            label={
              <Typography.Paragraph style={isMobile ? { fontSize: '16px', fontWeight: 'bold' } : { fontSize: '18px', fontWeight: '500' }}>
                Is the credential you uploaded in English?
              </Typography.Paragraph>
            }
            rules={[
              {
                required: true,
                message: 'Please select an option'
              }
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Radio.Group
              disabled={!revisionRequested.requestedChanges?.requestUploadTranslation}
              defaultValue={application?.isCredentialInEnglish ?? true}
              size="large"
              style={{ marginLeft: '10px' }}
            >
              <Space direction="vertical" style={{ rowGap: '0px' }}>
                <Radio value={true}>
                  <Typography.Text strong>Yes,</Typography.Text> it is in English in Latin characters
                </Radio>
                <Radio value={false}>
                  <Typography.Text strong>No,</Typography.Text> I'll provide a translation
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {!isCredentialInEnglish && (
            <Form.Item name={['uploadCredentialTranslation']}>
              <CredCaseFileUploadButtonWithPreviewContainer
                caseId={props.data.id}
                title={''}
                buttonText={'Upload Revised Document'}
                permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                isPreviewOnly={!applicationFormState.isUpload || !props.revisionRequested.requestedChanges?.requestUploadTranslation}
                isShowFileName={false}
                permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
                fieldName={'translation'}
                fileName={'translation'}
                onUploadSuccess={(response) => {
                  props.handleOnUploadSuccess(response, 'translation');
                }}
              />
            </Form.Item>
          )}
        </Form>
      )}
      {!applicationFormState.isCredentialDetailsEditable && (
        <Descriptions
          title="Credential Details"
          labelStyle={labelStyle}
          layout="vertical"
          extra={
            <Button
              type="primary"
              onClick={() => dispatch({ type: 'edit-credential-details' })}
              disabled={!applicationFormState.requestedChanges.requestUpdatedCredentialDetails}
            >
              Edit
            </Button>
          }
          column={2}
        >
          <Descriptions.Item style={descriptionStyle} label="Degree Title">
            {application?.credentialTitle ?? 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item style={descriptionStyle} label="Program (if applicable)">
            {application?.program ?? 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item style={descriptionStyle} label="Name on Credential">
            {application?.nameOnCredential ?? 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item style={descriptionStyle} label="Credential Issued Date">
            {application?.dateCredentialIssuedAt ? formatDate(application?.dateCredentialIssuedAt) : 'N/A'}
          </Descriptions.Item>
        </Descriptions>
      )}
      {applicationFormState.isCredentialDetailsEditable && (
        <>
          <Typography.Text strong>Credential Details</Typography.Text>
          <Form
            style={{ width: '100%' }}
            form={form}
            initialValues={{
              credentialTitle: application?.credentialTitle,
              program: application?.program,
              nameOnCredential: application?.nameOnCredential,
              dateCredentialIssuedAt: application?.dateCredentialIssuedAt
            }}
            layout="vertical"
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, 180px)',
                gridAutoFlow: 'row',
                gap: '10px'
              }}
            >
              <Form.Item
                label="Degree Title"
                name={['credentialTitle']}
                rules={[
                  {
                    required: true,
                    message: 'Please enter a degree title'
                  }
                ]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Program (if applicable)" name={['program']}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, 180px)',
                gridAutoFlow: 'row',
                gap: '10px'
              }}
            >
              <Form.Item
                label="Name on Credential"
                name={['nameOnCredential']}
                rules={[
                  {
                    required: true,
                    message: 'Please enter a name on credential'
                  }
                ]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label="Date Credential Issued"
                name={['dateCredentialIssuedAt']}
                rules={[
                  {
                    required: true,
                    message: 'Please enter a credential issued date'
                  }
                ]}
                valuePropName="value"
                getValueProps={(value) => ({
                  value: value ? dayjs(value).utc() : ''
                })}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  maxDate={dayjs().utc()}
                  value={application?.dateCredentialIssuedAt ? formatDate(application?.dateCredentialIssuedAt) : ''}
                  format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                />
              </Form.Item>
            </div>
          </Form>
        </>
      )}
      {!applicationFormState.isIssuingInstitutionEditable && (
        <Descriptions
          title="Institution that Issued the Credential"
          labelStyle={labelStyle}
          layout="vertical"
          extra={
            <Button
              type="primary"
              onClick={() => dispatch({ type: 'edit-issuing-institution' })}
              disabled={!applicationFormState.requestedChanges.requestUpdatedIssuingInstitution}
            >
              Edit
            </Button>
          }
          column={1}
        >
          {application?.issuingInstitution?.issuingInstitution && (
            <>
              <Descriptions.Item label="Institution Name" style={descriptionStyle}>
                {application?.issuingInstitution?.entityName}
              </Descriptions.Item>
              <Descriptions.Item label="Address" style={descriptionStyle}>
                {application?.issuingInstitution?.issuingInstitution?.address?.addressLine1 && (
                  <>
                    {props.application?.issuingInstitution?.issuingInstitution?.address?.addressLine1}
                    <br />
                  </>
                )}
                {application?.issuingInstitution?.issuingInstitution?.address?.addressLine2 && (
                  <>
                    {application?.issuingInstitution?.issuingInstitution?.address?.addressLine2}
                    <br />
                  </>
                )}
                {[
                  application?.issuingInstitution?.issuingInstitution.address?.city,
                  application?.issuingInstitution?.issuingInstitution.address?.stateOrProvince,
                  application?.issuingInstitution?.issuingInstitution.address?.zipOrPostalCode,
                  application?.issuingInstitution?.issuingInstitution.address?.country
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Descriptions.Item>
            </>
          )}
          {!application?.issuingInstitution?.issuingInstitution && (
            <>
              <Descriptions.Item label="Institution Name" style={descriptionStyle}>
                {application?.otherIssuingInstitution?.institutionName}
                <br />
              </Descriptions.Item>
              <Descriptions.Item label="Address" style={descriptionStyle}>
                {application?.otherIssuingInstitution?.addressLine1 && (
                  <>
                    {application?.otherIssuingInstitution?.addressLine1}
                    <br />
                  </>
                )}
                {application?.otherIssuingInstitution?.addressLine2 && (
                  <>
                    {application?.otherIssuingInstitution?.addressLine2}
                    <br />
                  </>
                )}
                {[
                  application?.otherIssuingInstitution?.city,
                  application?.otherIssuingInstitution?.stateOrProvince,
                  application?.otherIssuingInstitution?.zipOrPostalCode,
                  application?.otherIssuingInstitution?.country
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      )}
      {applicationFormState.isIssuingInstitutionEditable && (
        <>
          <Typography.Text strong>Issuing Institution</Typography.Text>
          <Form
            style={{ width: '85%' }}
            form={form}
            initialValues={{
              issuingInstitution: application?.issuingInstitution?.entityName || (application.otherIssuingInstitution && 'Other'),
              otherIssuingInstitution: (application.otherIssuingInstitution && 'Other')
                ? {
                    institutionName: application.otherIssuingInstitution?.institutionName,
                    addressLine1: application.otherIssuingInstitution?.addressLine1,
                    addressLine2: application.otherIssuingInstitution?.addressLine2,
                    city: application.otherIssuingInstitution?.city,
                    zipOrPostalCode: application.otherIssuingInstitution?.zipOrPostalCode,
                    stateOrProvince: application.otherIssuingInstitution?.stateOrProvince,
                    country: application.otherIssuingInstitution?.country
                  }
                : {
                    institutionName: '',
                    addressLine1: '',
                    addressLine2: '',
                    city: '',
                    zipOrPostalCode: '',
                    stateOrProvince: '',
                    country: ''
                  }
            }}
            layout="vertical"
          >
            <Form.Item
              label="Institution Name"
              name={['issuingInstitution']}
              rules={[
                {
                  required: true,
                  message: 'Please enter an institution name'
                }
              ]}
            >
              <Select
                onSelect={(value) => {
                  if (value === 'other' || !value) {
                    setIsOtherIssuingInstitution(true);
                  } else {
                    setIsOtherIssuingInstitution(false);
                  }
                }}
                style={{ width: 300 }}
              >
                {props.entities
                  .filter((entities) => entities.isIssuingInstitution)
                  .map((isIssuingInstitution) => (
                    <Select.Option key={isIssuingInstitution.id} value={isIssuingInstitution.id}>
                      {isIssuingInstitution.entityName}
                    </Select.Option>
                  ))}
                <Select.Option key={'other'} value={'other'}>
                  Other
                </Select.Option>
              </Select>
            </Form.Item>
            {isOtherIssuingInstitution && (
              <>
                <div>
                  <Form.Item
                    label="Other Institution Name"
                    name={['otherIssuingInstitution', 'institutionName']}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter an institution name'
                      }
                    ]}
                  >
                    <Input style={{ width: 300 }} />
                  </Form.Item>
                  <Form.Item
                    label="Address Line 1"
                    name={['otherIssuingInstitution', 'addressLine1']}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter an address line 1'
                      }
                    ]}
                  >
                    <Input style={{ width: 300 }} />
                  </Form.Item>

                  <Form.Item label="Address Line 2" name={['otherIssuingInstitution', 'addressLine2']}>
                    <Input style={{ width: 300 }}/>
                  </Form.Item>
                </div>
                <Form.Item
                  label="Country"
                  name={['otherIssuingInstitution', 'country']}
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a country'
                    }
                  ]}
                >
                  <Select placeholder="Select a country" showSearch onChange={(value) => setSelectedCountry(value)} optionFilterProp="children" style={{ width: 300 }}>
                    {props.countryData.map((country) => (
                      <Select.Option key={country.countryCode} value={country.countryCode}>
                        {country.countryName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a state/province'
                      }
                    ]}
                  >
                    <StateSelect
                      style={{ width: '100%' }}
                      name={['otherIssuingInstitution', 'stateOrProvince']}
                      selectedCountry={selectedCountry}
                      countryData={props.countryData}
                    />
                  </Form.Item>
                  <Form.Item
                    label="City"
                    name={['otherIssuingInstitution', 'city']}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a city'
                      }
                    ]}
                  >
                    <Input style={{ width: 150 }} />
                  </Form.Item>
                  <Form.Item
                    label="Zip/Postal Code"
                    name={['otherIssuingInstitution', 'zipOrPostalCode']}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a zip/postal code'
                      }
                    ]}
                  >
                    <Input style={{ width: 150 }} />
                  </Form.Item>
                </div>
              </>
            )}
          </Form>
        </>
      )}
      <Typography.Text style={{ fontWeight: '400', fontSize: '15px' }}>Does the name on your credential match your passport?</Typography.Text>
      {application?.isNameOnCredentialMatchPassport ? (
        <Typography.Paragraph style={descriptionStyle}>
          <Typography.Text strong>Yes,</Typography.Text> the name on the Credential matches my Passport.{' '}
        </Typography.Paragraph>
      ) : (
        <Typography.Paragraph style={descriptionStyle}>
          <Typography.Text strong>No,</Typography.Text> I will provide a document that proves that name belongs to me.{' '}
          <br />
          <Typography.Text>Name documentation can be viewed on your profile's identity tab</Typography.Text>
        </Typography.Paragraph>
      )}
      <Descriptions title="Where do you want to send this credential?" labelStyle={labelStyle} layout="vertical">
        <Descriptions.Item style={{ paddingLeft: '8px' }} label="Institution Name">
          {application?.sendDestination?.entityName}
        </Descriptions.Item>
      </Descriptions>
      <div
        style={{
          width: isMobile ? '100%' : '85%',
          display: applicationFormState.isApplicationEditable ? 'flex' : 'none',
          justifyContent: 'space-between'
        }}
      >
        <Button
          onClick={() => {
            dispatch({ type: 'cancel' });
          }}
        >
          Cancel
        </Button>
        <div
          style={{
            display: 'flex',
            gap: '8px'
          }}
        >
          <Button onClick={handleSave}>Save</Button>
          <Button type="primary" onClick={handleSaveAndComplete}>
            Save and Complete
          </Button>
        </div>
      </div>
    </div>
  );
};
